import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  autoUpdate,
  offset,
  size,
  useFloating,
  type FloatingRootContext,
  type ReferenceType,
} from '@floating-ui/react';
import { Button } from '@hologram-dimension/button';
import { TextInput } from '@hologram-dimension/text-input';
import useAddTagCall from 'api/legacy/tags/useAddTagCall';
import useRenameTagCall from 'api/legacy/tags/useRenameTagCall';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState, type JSX } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import type { HologramSimIdsMap, TriggerRef } from 'types/common';
import currentLocale from 'utils/locale/currentLocale';
import useAnalytics from 'state/contexts/Analytics/useAnalytics';
import styles from './TagEditor.module.css';
import type { TagManagerTag } from './types';

interface TagEditorPropsInternal {
  tag?: TagManagerTag;
  onCancel: () => void;
  onSave: (tag: TagManagerTag) => void;
  onError: (error: string | null) => void;
  rootContext: FloatingRootContext<ReferenceType>;
  setTagEditor: any;
  padLeft: boolean;
  /**
   * The logged in organizationId
   */
  organizationId?: OrgId;
  usedTagNames: Set<string>;
  deviceIds?: Array<DeviceId>;
  selectedSimIds?: HologramSimIdsMap;
}

type JSXDiv = JSX.IntrinsicElements['div'];
export type TagEditorProps = TagEditorPropsInternal &
  Pick<JSXDiv, Exclude<keyof JSXDiv, keyof TagEditorPropsInternal>>;

interface FormProps {
  newName: string;
}

const TagEditor = forwardRef<TriggerRef, TagEditorProps>(
  (
    {
      tag,
      onCancel,
      onSave,
      setTagEditor,
      rootContext,
      padLeft,
      onError,
      organizationId,
      usedTagNames,
      deviceIds,
      selectedSimIds,
      ...rest
    },
    ref
  ) => {
    const { register, handleSubmit, setFocus, getValues } = useForm<FormProps>({
      defaultValues: { newName: tag?.name ?? '' },
    });

    const { floatingStyles, context } = useFloating<HTMLFormElement>({
      rootContext,
      whileElementsMounted: autoUpdate,
      middleware: [
        size({
          apply({ elements, rects }) {
            const floatingEle = elements.floating;
            const referenceRect = rects.reference;
            floatingEle.style.height = `36px`;
            floatingEle.style.width = `${padLeft ? referenceRect.width - 36 : referenceRect.width - 6}px`;
          },
        }),
        offset(({ rects }) => ({
          mainAxis: -rects.reference.height / 2 - rects.floating.height / 2,
          crossAxis: padLeft ? 10 : 1,
        })),
      ],
    });

    const addTag = useAddTagCall({ orgId: organizationId });
    const renameTag = useRenameTagCall({ tagId: tag?.id, orgId: tag?.orgId });

    const [processing, setProcessing] = useState(false);

    useEffect(() => {
      setTimeout(() => {
        setFocus('newName', { shouldSelect: true });
      }, 0);
    }, [setFocus]);

    const onSubmit = useCallback<SubmitHandler<FormProps>>(
      async (data) => {
        onError(null);

        if (tag && data.newName === tag.name) {
          setFocus('newName', { shouldSelect: true });
          return;
        }

        if (data.newName.trim().length === 0) {
          setFocus('newName');
          return;
        }

        if (usedTagNames.has(data.newName.toLocaleLowerCase(currentLocale))) {
          onError('A tag already exists with this name. Please use a different name.');
          setFocus('newName', { shouldSelect: true });
          return;
        }

        setProcessing(true);

        let result;

        try {
          if (tag) {
            result = await renameTag(data.newName);
          } else {
            result = await addTag(data.newName, deviceIds);
          }
        } catch (err) {
          setProcessing(false);
          onError('There was a problem renaming this tag. Please try again.');
          setFocus('newName', { shouldSelect: true });
          return;
        }

        if (result?.success) {
          if (tag) {
            onSave({ ...tag, name: data.newName });
          } else {
            onSave({
              name: data.newName,
              id: result.data.tags[0].id,
              orgId: organizationId,
              selectedSimCount: selectedSimIds?.size ?? 0,
              sims:
                selectedSimIds && selectedSimIds.size
                  ? [...selectedSimIds.values()].map((id) => ({ id }))
                  : [],
            });
          }
          return;
        }

        onError('There was a problem renaming this tag. Please try again.');
        setProcessing(false);
        setFocus('newName', { shouldSelect: true });
      },
      [
        addTag,
        organizationId,
        deviceIds,
        onError,
        onSave,
        renameTag,
        selectedSimIds,
        setFocus,
        tag,
        usedTagNames,
      ]
    );

    useImperativeHandle(
      ref,
      () => ({
        trigger: () => {
          if (tag ? getValues().newName !== tag.name : getValues().newName.trim().length > 0) {
            setTimeout(() => {
              setFocus('newName', { shouldSelect: true });
            }, 0);
          } else {
            onCancel();
          }
        },
      }),
      [getValues, onCancel, setFocus, tag]
    );

    const { sendAnalyticsEvent } = useAnalytics();

    let confirmText: string = 'Add tag';

    if (tag) {
      confirmText = 'Rename tag';
    } else if (selectedSimIds) {
      if (selectedSimIds.size > 1) {
        confirmText = `Add tag to ${selectedSimIds.size} SIMs`;
      }
    }

    return (
      <FloatingPortal>
        <FloatingOverlay lockScroll className={styles.overlay}>
          <FloatingFocusManager context={context} initialFocus={setTagEditor}>
            <div ref={setTagEditor} {...rest} style={floatingStyles}>
              <form className={styles.wrapper} onSubmit={handleSubmit(onSubmit)}>
                <TextInput
                  id="tag-editor"
                  type="text"
                  className={styles.text}
                  inputProps={{ autoComplete: 'off', maxLength: 32 }}
                  {...register('newName')}
                />
                <Button onClick={onCancel} variant="secondary" className={styles.button}>
                  Cancel
                </Button>
                <Button
                  className={styles.button}
                  loading={processing}
                  type="submit"
                  onClick={() => {
                    if (sendAnalyticsEvent) sendAnalyticsEvent('Manage tags - Add tag');
                  }}
                >
                  {confirmText}
                </Button>
              </form>
            </div>
          </FloatingFocusManager>
        </FloatingOverlay>
      </FloatingPortal>
    );
  }
);

export default TagEditor;
